@if (vm$ | async; as vm) {
  <div class="em-dashboard">
    <!-- Toolbar -->
    <header class="em-toolbar">
      <div class="em-toolbar__heading">
        <h1 class="em-page-title">Admin</h1>
        <p class="em-page-sub">Here's what needs your attention today</p>
      </div>
      @if (access.fullAdminOrg || access.customerProfiles) {
        <div class="em-toolbar__actions">
          @if (access.fullAdminOrg) {
            <a routerLink="/admin/resellers/create" class="em-btn em-btn-ghost">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
              </svg>
              Add reseller
            </a>
            <a routerLink="/admin/users/create" class="em-btn em-btn-ghost">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
              </svg>
              Add user
            </a>
          }
          @if (access.customerProfiles) {
            <a routerLink="/admin/customer-profiles" class="em-btn em-btn-primary">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
              </svg>
              Create profile
            </a>
          }
        </div>
      }
    </header>

    <!-- KPI cards -->
    <section class="em-stats" aria-label="Summary" [style.--em-stat-cols]="statCardCount">
      <em-dashboard-stat-card [value]="vm.openRequests" label="Open requests" tone="amber" accent>
        <svg icon width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M10.3 3.9 2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" stroke="#e08a1e" stroke-width="1.7" stroke-linejoin="round"/>
          <path d="M12 8v5M12 16h.01" stroke="#e08a1e" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </em-dashboard-stat-card>

      @if (canApprovePos) {
        <em-dashboard-stat-card [value]="vm.pendingApprovals" label="Pending approvals" tone="blue">
          <svg icon width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M7 3h7l5 5v11a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" stroke="#17a2e0" stroke-width="1.7" stroke-linejoin="round"/>
            <path d="M14 3v5h5M9 14.5l2 2 4-4" stroke="#17a2e0" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </em-dashboard-stat-card>
      }

      @if (access.customerProfiles) {
        <em-dashboard-stat-card [value]="vm.profilesToReview" label="Profiles to review" tone="blue">
          <svg icon width="22" height="22" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="5" width="18" height="14" rx="2" stroke="#17a2e0" stroke-width="1.8"/>
            <circle cx="8.5" cy="11" r="2" stroke="#17a2e0" stroke-width="1.7"/>
            <path d="M13 10h5M13 13h4" stroke="#17a2e0" stroke-width="1.7" stroke-linecap="round"/>
          </svg>
        </em-dashboard-stat-card>
      }

      @if (access.fullAdminOrg) {
        <em-dashboard-stat-card [value]="vm.pricingRequests" label="Pricing requests" tone="amber">
          <svg icon width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M3.6 11.5 11 4.1a2 2 0 0 1 1.4-.6H18a2 2 0 0 1 2 2v5.6a2 2 0 0 1-.6 1.4l-7.4 7.4a2 2 0 0 1-2.8 0l-4.6-4.6a2 2 0 0 1 0-2.8Z" stroke="#d2901f" stroke-width="1.7" stroke-linejoin="round"/>
            <circle cx="15.4" cy="8.6" r="1.3" fill="#d2901f"/>
          </svg>
        </em-dashboard-stat-card>
      }

      <em-dashboard-stat-card [value]="vm.newProjects" label="New projects" tone="green">
        <svg icon width="23" height="23" viewBox="0 0 24 24" fill="none">
          <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" stroke="#25a35e" stroke-width="1.7" stroke-linejoin="round"/>
        </svg>
      </em-dashboard-stat-card>
    </section>

    <!-- Two-column body -->
    <div class="em-main">
      <!-- Open requests feed -->
      <section class="em-panel">
        <div class="em-panel__head">
          <h2 class="em-panel__title">Open requests</h2>
          @if (vm.openRequests) {
            <span class="em-count-badge">{{ vm.openRequests }}</span>
          }
          <div class="em-pills">
            @for (filter of filters; track filter.value) {
              <button
                type="button"
                class="dashboard-filter"
                [class.dashboard-filter--active]="vm.query.filter === filter.value"
                [attr.aria-pressed]="vm.query.filter === filter.value"
                [attr.data-testid]="'feed-filter-' + filter.value"
                (click)="onSelectFilter(filter.value)"
              >
                {{ filter.label }}
              </button>
            }
          </div>
        </div>

        @if (vm.feed.rows.length > 0) {
          <div class="em-feed" [class.em-feed--loading]="vm.feed.loading" [attr.aria-busy]="vm.feed.loading">
            @for (row of vm.feed.rows; track row.id) {
              <em-dashboard-request-row [row]="row" (open)="onOpenRequest(row)"/>
            }
          </div>
          @if (vm.feed.meta) {
            <em-dashboard-pager
              [meta]="vm.feed.meta"
              itemsName="requests"
              [limitOptions]="feedLimitOptions"
              (pageChange)="onPageChange($event)"
              (limitChange)="onLimitChange($event)"
            />
          }
        } @else if (vm.feed.loading) {
          <div class="em-feed-loading" aria-busy="true">Loading requests…</div>
        } @else {
          <div class="em-empty">
            <div class="em-empty__icon dashboard-empty-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M5 13l4 4 10-10" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="em-empty__title">You're all caught up</div>
            <div class="em-empty__sub">{{ emptyMessage(vm.query.filter) }}</div>
          </div>
        }
      </section>

      <!-- Right rail -->
      <aside class="em-rail">
        <section class="em-panel">
          <div class="em-panel__head">
            <h2 class="em-panel__title">Recent projects</h2>
            <a routerLink="/admin/projects" class="em-link">
              View all
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </a>
          </div>
          <div class="em-list">
            @for (project of vm.recentProjects; track project.id) {
              <a [routerLink]="project.link" class="em-list-row">
                <span class="em-list-row__well">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" stroke="#5a6a7c" stroke-width="1.7" stroke-linejoin="round"/>
                  </svg>
                </span>
                <span class="em-list-row__body">
                  <span class="em-list-row__name">{{ project.name }}</span>
                  @if (project.subtitle) {
                    <span class="em-list-row__sub">{{ project.subtitle }}</span>
                  }
                </span>
                <span class="em-list-row__time">{{ project.timestamp }}</span>
              </a>
            } @empty {
              <div class="em-list-empty">No projects yet.</div>
            }
          </div>
        </section>

        <section class="em-panel">
          <div class="em-panel__head">
            <h2 class="em-panel__title">Manage catalog</h2>
            @if (access.estimateSettings) {
              <a routerLink="/admin/alarm-limits" class="em-catalog-row">Alarm limit defaults</a>
            }
          </div>
          <nav class="em-list">
            @if (access.batteryCatalog) {
              <a routerLink="/admin/batteries" class="em-catalog-row">
                <span class="em-list-row__well">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="8" width="15" height="9" rx="2" stroke="#5a6a7c" stroke-width="1.7"/>
                    <path d="M21 11v3" stroke="#5a6a7c" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </span>
                <span class="em-catalog-row__name">Batteries</span>
                <svg class="em-catalog-row__chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </a>
              <a routerLink="/admin/cabinets" class="em-catalog-row">
                <span class="em-list-row__well">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                    <rect x="4" y="3" width="16" height="18" rx="2" stroke="#5a6a7c" stroke-width="1.7"/>
                    <path d="M4 9h16M4 15h16" stroke="#5a6a7c" stroke-width="1.6"/>
                  </svg>
                </span>
                <span class="em-catalog-row__name">Cabinets</span>
                <svg class="em-catalog-row__chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </a>
            }
            @if (access.estimateSettings) {
              <a routerLink="/admin/settings" class="em-catalog-row"><span class="em-catalog-row__name">Admin settings</span></a>
              <a routerLink="/admin/notification-settings" class="em-catalog-row"><span class="em-catalog-row__name">Notification settings</span></a>
            }
            @if (access.fullAdminOrg) {
              <a routerLink="/admin/part-numbers" class="em-catalog-row">
                <span class="em-list-row__well">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                    <path d="M3.6 11.5 11 4.1a2 2 0 0 1 1.4-.6H18a2 2 0 0 1 2 2v5.6a2 2 0 0 1-.6 1.4l-7.4 7.4a2 2 0 0 1-2.8 0l-4.6-4.6a2 2 0 0 1 0-2.8Z" stroke="#5a6a7c" stroke-width="1.7" stroke-linejoin="round"/>
                    <circle cx="15.4" cy="8.6" r="1.3" fill="#5a6a7c"/>
                  </svg>
                </span>
                <span class="em-catalog-row__name">Products</span>
                <svg class="em-catalog-row__chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </a>
              <a routerLink="/admin/cables" class="em-catalog-row">
                <span class="em-list-row__well">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                    <path d="M6 4v8a4 4 0 0 0 8 0V8a3 3 0 0 1 6 0v8" stroke="#5a6a7c" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
                <span class="em-catalog-row__name">Cables</span>
                <svg class="em-catalog-row__chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </a>
            }
          </nav>
        </section>
      </aside>
    </div>
  </div>
}
